import { FC, useState, useMemo, useRef, Fragment } from 'react';
import Spacer from '@components/Spacer';
import { pickProductIcon } from '@components/ProductIcon';
import pluralize from 'pluralize';
import Buttons from '@components/Buttons/Buttons';
import * as ApiModels from '@typings/api-models';
import ProgressIndicator from '@components/ProgressIndicator';
import CollapsibleArea from '@components/CollapsibleArea';
import SearchInput from '@/components/FormFields/SearchInput';
import useHighlightSearchTerm from '@/hooks/useHighlightSearchTerm';

import s from './InitiateCaseModal.module.scss';

type Props = {
  fullProducts: Array<
    Pick<ApiModels.Product, 'id' | 'title' | 'initiateCaseRank'> & {
      productTypes: Array<Pick<ApiModels.ProductType, 'noodleProductType'>>;
      workflows: Array<Pick<ApiModels.Workflow, 'id' | 'name'>>;
    }
  >;
  onWorkflowSelect: (workflowId: string) => void;
  isFetching: boolean;
  associatedUserWorkflowId?: string | null;
};

const SelectWorkflow: FC<Props> = ({ isFetching, fullProducts, onWorkflowSelect, associatedUserWorkflowId }) => {
  const [search, setSearch] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredProducts = useMemo(() => {
    const lowerCaseSearch = search.toLowerCase();

    if (search.length > 2) {
      return fullProducts
        .map(product => {
          // if the product title matches the search term, include all product's workflows
          if (product.title?.toLowerCase().includes(lowerCaseSearch)) {
            const filteredWorkflows = product.workflows.filter(workflow => workflow.id !== associatedUserWorkflowId);
            return { ...product, workflows: filteredWorkflows };
          }

          const filteredWorkflows = product.workflows.filter(
            workflow => workflow.name.toLowerCase()
              .includes(lowerCaseSearch) && workflow.id !== associatedUserWorkflowId,
          );
          return { ...product, workflows: filteredWorkflows };
        })
        .filter(product => product.workflows.length > 0)
        .sort((a, b) => (a.initiateCaseRank ?? 999) - (b.initiateCaseRank ?? 999));
    }

    return fullProducts;
  }, [search, fullProducts, associatedUserWorkflowId]);

  useHighlightSearchTerm(search, containerRef.current ? [containerRef.current] : []);

  return (
    <div className={s.workflowSelect}>
      {isFetching
        ? (
          <ProgressIndicator isCentered />
        )
        : (
          <>
            <SearchInput placeholder="Search for a workflow" autoFocus label="Search for a workflow" hideLabel setSearch={setSearch} />
            <p className="caption" style={{ color: 'var(--color-gray-75)' }}>
              Select product
            </p>
            <Spacer size="12px" />
            <div className={s.products} ref={containerRef}>
              {filteredProducts.length === 0 && <p className="body-sm">No workflows found.</p>}
              {filteredProducts.map(product => {
                const Icon = pickProductIcon(product.productTypes?.[0]?.noodleProductType);
                return (
                  <Fragment key={`${product.id}-${search.length > 2 ? 'open' : 'closed'}`}>
                    <CollapsibleArea
                      title={product.title as string}
                      description={pluralize('workflow', product.workflows.length, true)}
                      key={product.id}
                      Icon={Icon ? { Component: Icon, color: 'var(--color-noodle)' } : undefined}
                      className={s.details}
                      open={search.length > 2}
                    >
                      <Spacer size="16px" />
                      <div className={s.workflows}>
                        {product.workflows.map(workflow => (
                          <Buttons key={workflow.id} isWrapper onClick={() => onWorkflowSelect(workflow.id)}>
                            <div className={s.workflow}>
                              <p className="caption">{workflow.name}</p>
                              <p className="caption-bold" style={{ color: 'var(--color-noodle)' }}>
                                Initiate
                              </p>
                            </div>
                          </Buttons>
                        ))}
                      </div>
                    </CollapsibleArea>
                  </Fragment>
                );
              })}
            </div>
          </>
        )}
    </div>
  );
};

export default SelectWorkflow;
